import React from 'react';

type Props = { children: React.ReactNode; fallbackTitle?: string; componentName?: string };
type State = { hasError: boolean; message: string; stack: string };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: '', stack: '' };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message || 'Unknown error', stack: err.stack || '' };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    // Surface in console for diagnosis without crashing the shell
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', err, info);
  }

  reset = () => this.setState({ hasError: false, message: '', stack: '' });

  render() {
    if (this.state.hasError) {
      return (
        <div className="m-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6">
          <div className="text-xs font-black uppercase tracking-widest text-amber-400 mb-2">
            {this.props.fallbackTitle || 'This panel hit an error'}
          </div>
          <div className="text-sm text-amber-200 mb-3">{this.state.message}</div>
          <details className="text-[10px] text-amber-300/60 mb-3 font-mono whitespace-pre-wrap break-all"><summary className="cursor-pointer underline mb-1">Show stack</summary>{this.state.stack}</details>
          <div className="text-[11px] text-amber-300/70 mb-4">
            The rest of the app keeps running. Try selecting a different tab or symbol.
          </div>
          <button
            onClick={this.reset}
            className="px-3 py-1.5 rounded-lg bg-amber-500 text-amber-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children as any;
  }
}

export default ErrorBoundary;
